import { create } from "zustand";
import Auth from "../Auth";

type AdminRole = "support" | "admin" | null;
type AdminAccessResponse = {
  success: boolean;
  // The API returns the effective customer ID here; authUid below is the credential.
  userId: string;
  role: AdminRole;
  permissions: string[];
  effectiveUser?: { uid: string; email?: string; displayName?: string; photoURL?: string };
  impersonation?: { sessionId: string; actorId: string; expiresAt: string };
};
type AdminAccessState = {
  authUid: string | null;
  role: AdminRole;
  permissions: string[];
  loading: boolean;
  error: string | null;
  effectiveUser: AdminAccessResponse["effectiveUser"] | null;
  impersonation: AdminAccessResponse["impersonation"] | null;
  load: (authUid: string | null) => Promise<AdminAccessResponse | null>;
  reset: () => void;
};

/**
 * In-memory UI capabilities, keyed by the raw Firebase credential UID.
 * Express authorizes every operation independently. Do not persist this store.
 * `loading` covers initial/changed identity bootstrap; same-user refresh keeps
 * the current screen mounted. A denied/failed response removes capabilities.
 * Generation numbers discard results from a prior identity or reset.
 */
let generation = 0;
let pending: Promise<AdminAccessResponse | null> | null = null;
const emptyAccess = {
  authUid: null,
  role: null,
  permissions: [],
  loading: false,
  error: null,
  effectiveUser: null,
  impersonation: null,
};
export const useAdminAccessStore = create<AdminAccessState>((set, get) => ({
  ...emptyAccess,
  reset() {
    generation += 1;
    pending = null;
    set(emptyAccess);
  },
  async load(authUid) {
    if (!authUid) {
      get().reset();
      return null;
    }
    // Every call starts a fresh lookup unless this identity already has one in
    // flight. Entry/focus refreshes share bootstrap's promise instead of making
    // its eventual session result obsolete.
    if (authUid === get().authUid && pending) return pending;
    const requestGeneration = ++generation;
    // A same-user refresh must not unmount admin forms or remove the session
    // banner while the request is pending. Keep the last verified UI state;
    // Express still authorizes every action. Identity switches clear it at once.
    set(authUid === get().authUid ? { error: null } : { ...emptyAccess, authUid, loading: true });
    const request = Auth.fetchWithIdToken("/api/admin-roles/access")
      .then((response: AdminAccessResponse) => {
        if (requestGeneration !== generation) return null;
        // Invalid or absent roles never get capabilities, even if a malformed
        // response includes permission strings. Impersonation has role null.
        const role =
          response?.role === "admin" || response?.role === "support" ? response.role : null;
        const permissions =
          response?.success && role && Array.isArray(response.permissions)
            ? response.permissions.filter((permission) => typeof permission === "string")
            : [];
        set({
          role: permissions.length ? role : null,
          permissions,
          loading: false,
          effectiveUser: response?.success ? response.effectiveUser || null : null,
          impersonation: response?.success ? response.impersonation || null : null,
        });
        return response?.success ? response : null;
      })
      .catch(() => {
        if (requestGeneration === generation) {
          set({ ...emptyAccess, authUid, error: "Unable to verify admin access." });
        }
        return null;
      })
      .finally(() => {
        if (requestGeneration === generation) pending = null;
      });
    pending = request;
    return request;
  },
}));

export function hasAdminPermission(permission: string): boolean {
  return useAdminAccessStore.getState().permissions.includes(permission);
}
